<script setup lang="ts">
import { computed, toRefs } from 'vue';
import { useOrganizationContractsStore } from '@/store';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputNumber from 'primevue/inputnumber';
import InputSwitch from 'primevue/inputswitch';
import RadioButton from 'primevue/radiobutton';
import Message from 'primevue/message';
import { ContractTherapyEvaluationTypeInterface } from '@/types';
import useVuelidate from '@vuelidate/core';
import { helpers, requiredIf } from '@vuelidate/validators';
import { can, useDateTime } from '@/composables';
import { PERMISSIONS } from '@/enum';

interface Props {
  scope: string;
}

const props = defineProps<Props>();

const { scope } = toRefs(props);

const organizationContractsStore = useOrganizationContractsStore();

const isToggledOn = (v: string, data: ContractTherapyEvaluationTypeInterface) => {
  return !!data.toggle;
};

const rules = computed(() => {
  return {
    evaluations: {
      $each: helpers.forEach({
        toggle: {},
        window_length: {
          requiredIfCustom: helpers.withMessage('Window Length of 0 or greater is required', requiredIf(isToggledOn)),
        },
        noshow_rate: {
          requiredIfCustom: helpers.withMessage('No Show of 0 or greater is required', requiredIf(isToggledOn)),
        },
        rate: {
          requiredIfCustom: helpers.withMessage('Rate of 0 or greater is required', requiredIf(isToggledOn)),
        },
        pre_purchased: {
          requiredIfCustom: helpers.withMessage('Pre Purchased of 0 or greater is required', requiredIf(isToggledOn)),
        },
        included: {
          requiredIfCustom: helpers.withMessage('Included Evals of 0 or greater is required', requiredIf(isToggledOn)),
        },
      }),
    },
  };
});

const v$ = useVuelidate(
  rules,

  { evaluations: organizationContractsStore.contractEvaluationTypes },
  { $scope: scope.value },
);

const onToggleChange = async (data: ContractTherapyEvaluationTypeInterface) => {
  data.disabled = !data.toggle;
  if (data.disabled === true) checkToggleCount(data);
  else {
    resetValidSetting();
  }

  await checkEventToRemove(data);
};

const checkEventToRemove = async (data: ContractTherapyEvaluationTypeInterface): Promise<void> => {
  const id = data.therapy_evaluation_type_id;

  if (
    !organizationContractsStore.contract?.id ||
    !organizationContractsStore.isToggleTrueInOriginalEvaluationsTypes(id)
  )
    return;

  if (data.toggle) {
    setEventRemoval(id, undefined);
    return;
  }

  const { events_count = 0 } = await organizationContractsStore.checkEventToRemove({ evaluation_type_id: id });
  setEventRemoval(id, events_count > 0 ? events_count : undefined);
};

const setEventRemoval = (id: number, value: number | undefined) => {
  organizationContractsStore.$patch((state) => {
    const found = state.contractEvaluationTypes.find((e) => e.therapy_evaluation_type_id === id);
    if (found && found.event_removal !== value) {
      found.event_removal = value;
    }
  });
};

//here we check to see if all toggles are off and if so set an error
const checkToggleCount = (data: ContractTherapyEvaluationTypeInterface) => {
  const count = organizationContractsStore.contractEvaluationTypes.find(
    (evaluation: ContractTherapyEvaluationTypeInterface) => evaluation.toggle,
  );

  if (!!count === false) data.isValid = false;
};

/**
 * if at least one toggle is on then all is valid
 */
const resetValidSetting = () => {
  organizationContractsStore.contractEvaluationTypes.forEach((evaluation: ContractTherapyEvaluationTypeInterface) => {
    evaluation.isValid = true;
  });
};

const checkNoShowType = (data: ContractTherapyEvaluationTypeInterface) => {
  if (!data.noshow_rate_is_fixed && data.noshow_rate > 100) {
    data.noshow_rate = 100;
  }
};

const maxNoShow = (data: ContractTherapyEvaluationTypeInterface) => {
  return data.noshow_rate_is_fixed ? 10000 : 100;
};

const checkNoShowTypeMode = (noshow_rate_is_fixed: boolean) => {
  return noshow_rate_is_fixed ? 'currency' : 'decimal';
};

const canEdit = computed(() => {
  return can([PERMISSIONS.CONTRACTEVALUATION_UPDATE]);
});

// Usage example:
</script>

<template>
  <DataTable
    v-if="organizationContractsStore.contractEvaluationTypes.length"
    :value="organizationContractsStore.contractEvaluationTypes"
    :loading="organizationContractsStore.loading"
    :row-key="'therapy_evaluation_type_id'"
    class="p-datatable-gridlines"
  >
    <template #header>
      <div class="flex mb-1">
        <p><b>Evaluations</b></p>
      </div>
    </template>
    <Column style="width: 6%">
      <template #body="slotProps">
        <InputSwitch
          v-model="slotProps.data.toggle"
          :class="{
            'p-invalid': !!v$.evaluations.$each.$response?.$errors?.[slotProps.index]?.isValid?.$invalid,
          }"
          :disabled="!canEdit"
          @change="onToggleChange(slotProps.data)"
        />
        <div
          v-for="(error, i) in v$.evaluations.$each.$response?.$errors?.[slotProps.index]?.isValid ?? []"
          :key="i"
          class="input-errors"
        >
          <div class="p-error">{{ error.$message }}</div>
        </div>
      </template>
    </Column>

    <Column field="title" header="Type" style="width: 20%" body-class="wrap-cell">
      <template #body="slotProps">
        <span>{{ slotProps.data.title }}</span>
        <Message
          v-if="slotProps.data.event_removal && slotProps.data.event_removal > 0"
          :closable="false"
          class="contract-date-notice border-primary justify-content-start my-1"
          severity="warn"
          icon="none"
        >
          <div class="flex align-items-center">
            <Icon name="warning" class="text-color-yellow-dark text-lg align-self-start" />
            <p class="ml-2 text-color-red-dark">
              Turning off the toggle will remove
              <b>{{ slotProps.data.event_removal }}</b>
              scheduled events for this evaluation type.
              <b>
                {{ useDateTime().formatIntlDate(organizationContractsStore.contract.start_at) }} -
                {{ useDateTime().formatIntlDate(organizationContractsStore.contract.end_at) }}
              </b>
              .
            </p>
          </div>
        </Message>
      </template>
    </Column>
    <Column header="Flat Fee" style="width: 12%">
      <template #body="slotProps">
        <InputNumber
          v-model="slotProps.data.rate"
          :class="{
            'p-invalid': !!v$.evaluations.$each.$response?.$errors?.[slotProps.index]?.rate?.$invalid,
          }"
          mode="currency"
          currency="USD"
          :disabled="!slotProps.data.toggle || !canEdit"
          :min="0"
          @blur="v$.$touch"
        />
        <div v-if="slotProps.data.toggle">
          <div
            v-for="(error, i) in v$.evaluations.$each.$response?.$errors?.[slotProps.index]?.rate ?? []"
            :key="i"
            class="input-errors"
          >
            <div class="p-error">{{ error.$message }}</div>
          </div>
        </div>
      </template>
    </Column>
    <Column header="No Show / Late Cancel %/$" :style="{ width: '20%' }">
      <template #body="slotProps">
        <div class="flex flex-column">
          <div class="flex align-items-center gap-1">
            <InputNumber
              v-model="slotProps.data.noshow_rate"
              :mode="checkNoShowTypeMode(!!slotProps.data.noshow_rate_is_fixed)"
              currency="USD"
              :disabled="!slotProps.data.toggle || !canEdit"
              :class="{
                'p-invalid': !!v$.evaluations.$each.$response?.$errors?.[slotProps.index]?.noshow_rate?.$invalid,
              }"
              :min="0"
              :max="maxNoShow(slotProps.data)"
              @blur="v$.$touch"
              @input="checkNoShowType(slotProps.data)"
            />
            <div class="flex flex-column gap-1">
              <div class="flex align-items-center">
                <RadioButton
                  v-model="slotProps.data.noshow_rate_is_fixed"
                  :value="true"
                  :disabled="!slotProps.data.toggle || !canEdit"
                  @change="checkNoShowType(slotProps.data)"
                />
                <label class="ml-2">$</label>
              </div>
              <div class="flex align-items-center">
                <RadioButton
                  v-model="slotProps.data.noshow_rate_is_fixed"
                  :value="false"
                  :disabled="!slotProps.data.toggle || !canEdit"
                  @change="checkNoShowType(slotProps.data)"
                />
                <label class="ml-2">%</label>
              </div>
            </div>
          </div>
          <div v-if="slotProps.data.toggle">
            <div
              v-for="(error, i) in v$.evaluations.$each.$response?.$errors?.[slotProps.index]?.noshow_rate ?? []"
              :key="i"
              class="input-errors"
            >
              <div class="p-error">{{ error.$message }}</div>
            </div>
          </div>
        </div>
      </template>
    </Column>
    <Column header="Window" style="width: 11%">
      <template #body="slotProps">
        <InputNumber
          v-model="slotProps.data.window_length"
          :disabled="!slotProps.data.toggle || !canEdit"
          :class="{
            'p-invalid': !!v$.evaluations.$each.$response?.$errors?.[slotProps.index]?.window_length?.$invalid,
          }"
          :min="0"
          @blur="v$.$touch"
        />
        <div v-if="slotProps.data.toggle">
          <div
            v-for="(error, i) in v$.evaluations.$each.$response?.$errors?.[slotProps.index]?.window_length ?? []"
            :key="i"
            class="input-errors"
          >
            <div class="p-error">{{ error.$message }}</div>
          </div>
        </div>
      </template>
    </Column>
    <Column header="Pre Purchased" style="width: 13%">
      <template #body="slotProps">
        <InputNumber
          v-model="slotProps.data.pre_purchased"
          :disabled="!slotProps.data.toggle || !canEdit"
          :class="{
            'p-invalid': !!v$.evaluations.$each.$response?.$errors?.[slotProps.index]?.pre_purchased?.$invalid,
          }"
          :min="0"
          @blur="v$.$touch"
        />
        <div v-if="slotProps.data.toggle">
          <div
            v-for="(error, i) in v$.evaluations.$each.$response?.$errors?.[slotProps.index]?.pre_purchased ?? []"
            :key="i"
            class="input-errors"
          >
            <div class="p-error">{{ error.$message }}</div>
          </div>
        </div>
      </template>
    </Column>
    <Column header="Included Evals" style="width: 13%">
      <template #body="slotProps">
        <InputNumber
          v-model="slotProps.data.included"
          :disabled="!slotProps.data.toggle || !canEdit"
          :class="{
            'p-invalid': !!v$.evaluations.$each.$response?.$errors?.[slotProps.index]?.included?.$invalid,
          }"
          :min="0"
          :max-fraction-digits="0"
          @blur="v$.$touch"
        />
        <div v-if="slotProps.data.toggle">
          <div
            v-for="(error, i) in v$.evaluations.$each.$response?.$errors?.[slotProps.index]?.included ?? []"
            :key="i"
            class="input-errors"
          >
            <div class="p-error">{{ error.$message }}</div>
          </div>
        </div>
      </template>
    </Column>
  </DataTable>

  <div v-else>...Loading</div>
</template>

<style lang="scss" scoped>
.p-datatable-gridlines .p-datatable-tbody > tr > td {
  border: 1px solid #dee2e6;
}

.p-disabled,
.p-component:disabled {
  pointer-events: none;
  opacity: 0.8;
}

:deep(.p-datatable-table) {
  table-layout: fixed;
}

:deep(.wrap-cell) {
  white-space: normal;
  word-break: break-word;
}

:deep(.p-inputnumber),
:deep(.p-inputnumber-input) {
  width: 100%;
  min-width: 0;
}
</style>
